import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { useRecoilValue, useSetRecoilState } from "recoil";
import MessageItem from "./MessageItem";
import "./MessageList.css";
import {
  chatComposerTextState,
  chatCurrentUserIdState,
  chatEditingMessageIdState,
  chatMessagesByRoomState,
  chatMessagesRefreshState,
  selectedChatMessagesState,
  selectedChatRoomState,
} from "../../state/chatState";
import apiService from "../../../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";
import { getChatRoomId } from "../../chatRoomUtils";
import { getFullName } from "../../../careprovider/visits/utils";
import { normalizeApiMessages, prependOlderMessages, applyUserReaction, buildRoomMemberDirectory, isDisplayableChatMessage } from "../../chatMessageUtils";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";

const SCROLL_TOP_THRESHOLD_PX = 80;

const createPaginationState = (roomId) => ({
  roomId,
  page: 1,
  hasMoreMessages: true,
  loadedPages: new Set(),
});

const MessageList = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userJson = useReadMemory(MemoryType.USER);
  const messages = useRecoilValue(selectedChatMessagesState);
  const displayMessages = useMemo(
    () => messages.filter(isDisplayableChatMessage),
    [messages],
  );
  const selectedRoom = useRecoilValue(selectedChatRoomState);
  const currentUserId = useRecoilValue(chatCurrentUserIdState);
  const messagesRefresh = useRecoilValue(chatMessagesRefreshState);
  const setMessagesByRoom = useSetRecoilState(chatMessagesByRoomState);
  const setComposerText = useSetRecoilState(chatComposerTextState);
  const setEditingMessageId = useSetRecoilState(chatEditingMessageIdState);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollStateRef = useRef({ roomId: null, messageIds: [] });
  const paginationRef = useRef(createPaginationState(null));
  const pendingScrollRestoreRef = useRef(null);
  const isLoadingOlderRef = useRef(false);
  const isPrependingRef = useRef(false);
  const skipAutoScrollRef = useRef(false);
  const requiresScrollAwayRef = useRef(false);
  const isInitialLoadCompleteRef = useRef(false);
  const reactingMessagesRef = useRef(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [reactingMessageId, setReactingMessageId] = useState(null);

  const roomId = selectedRoom ? getChatRoomId(selectedRoom) : null;

  const handleEditMessage = useCallback(
    (message) => {
      setEditingMessageId(message.messageId);
      setComposerText(message.content);
    },
    [setComposerText, setEditingMessageId],
  );

  const handleReactToMessage = useCallback(
    async (message, reaction) => {
      const messageId = message?.messageId ? String(message.messageId) : "";

      if (!roomId || !userJson?.userIdentifier || !messageId) {
        return;
      }

      if (reactingMessagesRef.current.has(messageId)) {
        return;
      }

      reactingMessagesRef.current.add(messageId);
      setReactingMessageId(messageId);

      let reactionPayload = reaction;

      setMessagesByRoom((prev) => {
        const target = (prev[roomId] || []).find(
          (item) => String(item.messageId) === messageId,
        );
        const existingReaction = target?.reactions?.find(
          (entry) =>
            String(entry.userIdentifier) === String(userJson.userIdentifier),
        )?.reaction;
        reactionPayload = existingReaction === reaction ? null : reaction;
        return prev;
      });

      const isRemoving = reactionPayload == null;

      try {
        await apiService.post(
          "niramayafoundation/api/v1/communication/message/recipient/reactions",
          {
            messageId: Number(messageId),
            roomId: Number(roomId),
            userIdentifier: userJson.userIdentifier,
            reaction: reactionPayload,
          },
          null,
          isRemoving ? "Could not remove reaction" : "Could not add reaction",
        );

        const userName =
          getFullName(userJson?.givenName, userJson?.familyName) ||
          userJson?.userIdentifier;

        setMessagesByRoom((prev) => {
          const roomMessages = prev[roomId];
          if (!roomMessages?.length) {
            return prev;
          }

          return {
            ...prev,
            [roomId]: roomMessages.map((item) =>
              String(item.messageId) === messageId
                ? {
                    ...item,
                    reactions: applyUserReaction(
                      item.reactions,
                      userJson.userIdentifier,
                      reactionPayload,
                      userName,
                    ),
                  }
                : item,
            ),
          };
        });
      } finally {
        reactingMessagesRef.current.delete(messageId);
        setReactingMessageId((current) =>
          String(current) === messageId ? null : current,
        );
      }
    },
    [
      roomId,
      setMessagesByRoom,
      userJson?.familyName,
      userJson?.givenName,
      userJson?.userIdentifier,
    ],
  );

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const messageIds = displayMessages.map((message) => message.messageId);
    const prev = scrollStateRef.current;
    const pending = pendingScrollRestoreRef.current;

    if (pending && container) {
      container.scrollTop =
        container.scrollHeight - pending.scrollHeight + pending.scrollTop;
      pendingScrollRestoreRef.current = null;
      isPrependingRef.current = false;
      skipAutoScrollRef.current = false;
      isLoadingOlderRef.current = false;
      requiresScrollAwayRef.current = true;
      scrollStateRef.current = { roomId, messageIds };
      return;
    }

    if (skipAutoScrollRef.current || isPrependingRef.current || isLoadingOlderRef.current) {
      scrollStateRef.current = { roomId, messageIds };
      return;
    }

    if (
      container &&
      !isLoading &&
      displayMessages.length > 0 &&
      roomId &&
      !isInitialLoadCompleteRef.current
    ) {
      container.scrollTop = container.scrollHeight;
      isInitialLoadCompleteRef.current = true;
      setIsInitialLoadComplete(true);
      scrollStateRef.current = { roomId, messageIds };
      return;
    }

    const isSameRoom = prev.roomId === roomId;
    const hasPreviousMessages = prev.messageIds.length > 0 && messageIds.length > 0;
    const firstMessageUnchanged =
      hasPreviousMessages && messageIds[0] === prev.messageIds[0];
    const lastMessageChanged =
      hasPreviousMessages &&
      messageIds[messageIds.length - 1] !== prev.messageIds[prev.messageIds.length - 1];
    const isAppendAtBottom =
      isSameRoom &&
      firstMessageUnchanged &&
      lastMessageChanged &&
      messageIds.length >= prev.messageIds.length;

    if (isAppendAtBottom && container) {
      container.scrollTop = container.scrollHeight;
    }

    scrollStateRef.current = { roomId, messageIds };
  }, [displayMessages, isLoading, roomId]);

  const loadOlderMessages = useCallback(async () => {
    const pagination = paginationRef.current;

    if (
      !organizationIdentifier ||
      !roomId ||
      !selectedRoom ||
      !isInitialLoadCompleteRef.current ||
      !pagination.hasMoreMessages ||
      isLoadingOlderRef.current ||
      pagination.roomId !== roomId
    ) {
      return;
    }

    const nextPage = pagination.page + 1;

    if (pagination.loadedPages.has(nextPage)) {
      return;
    }

    isPrependingRef.current = true;
    skipAutoScrollRef.current = true;
    isLoadingOlderRef.current = true;
    setIsLoadingOlderMessages(true);
    pagination.loadedPages.add(nextPage);

    try {
      const response = await apiService.get(
        "niramayafoundation/api/v1/communication/message",
        {
          organizationIdentifier,
          recipient: userJson?.userIdentifier,
          keywords: `room_${roomId}`,
          page: nextPage,
        },
      );

      if (!Array.isArray(response)) {
        pagination.loadedPages.delete(nextPage);
        isPrependingRef.current = false;
        skipAutoScrollRef.current = false;
        isLoadingOlderRef.current = false;
        return;
      }

      const normalizedMessages = normalizeApiMessages(response, selectedRoom);

      if (!normalizedMessages.length) {
        pagination.hasMoreMessages = false;
        isPrependingRef.current = false;
        skipAutoScrollRef.current = false;
        isLoadingOlderRef.current = false;
        return;
      }

      pagination.page = nextPage;

      const container = scrollContainerRef.current;
      if (container) {
        pendingScrollRestoreRef.current = {
          scrollHeight: container.scrollHeight,
          scrollTop: container.scrollTop,
        };
      }

      setMessagesByRoom((prev) => {
        const existing = prev[roomId] || [];
        const merged = prependOlderMessages(existing, normalizedMessages);

        if (merged.length === existing.length) {
          pendingScrollRestoreRef.current = null;
          isPrependingRef.current = false;
          skipAutoScrollRef.current = false;
          isLoadingOlderRef.current = false;
        }

        return {
          ...prev,
          [roomId]: merged,
        };
      });
    } catch {
      pagination.loadedPages.delete(nextPage);
      pendingScrollRestoreRef.current = null;
      isPrependingRef.current = false;
      skipAutoScrollRef.current = false;
      isLoadingOlderRef.current = false;
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }, [
    organizationIdentifier,
    roomId,
    selectedRoom,
    setMessagesByRoom,
    userJson?.userIdentifier,
  ]);

  const handleScroll = useCallback(() => {
    if (!isInitialLoadCompleteRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    if (container.scrollTop > SCROLL_TOP_THRESHOLD_PX) {
      requiresScrollAwayRef.current = false;
      return;
    }

    if (requiresScrollAwayRef.current || isLoadingOlderRef.current) {
      return;
    }

    loadOlderMessages();
  }, [loadOlderMessages]);

  useEffect(() => {
    if (!organizationIdentifier || !roomId || !selectedRoom) {
      return undefined;
    }

    let isCancelled = false;

    paginationRef.current = createPaginationState(roomId);
    isLoadingOlderRef.current = false;
    isPrependingRef.current = false;
    skipAutoScrollRef.current = false;
    requiresScrollAwayRef.current = false;
    isInitialLoadCompleteRef.current = false;
    setIsInitialLoadComplete(false);
    setIsLoadingOlderMessages(false);

    const loadMessages = async () => {
      setIsLoading(true);

      const response = await apiService.get(
        "niramayafoundation/api/v1/communication/message",
        {
          organizationIdentifier,
          recipient: userJson?.userIdentifier,
          keywords: `room_${roomId}`,
          page: 1,
        },
      );

      if (isCancelled) {
        return;
      }

      const pagination = paginationRef.current;
      pagination.loadedPages.add(1);

      if (!Array.isArray(response) || !response.length) {
        pagination.hasMoreMessages = false;
      }

      const normalizedMessages = normalizeApiMessages(
        Array.isArray(response) ? response : [],
        selectedRoom,
      );

      setMessagesByRoom((prev) => ({
        ...prev,
        [roomId]: normalizedMessages,
      }));
      setIsLoading(false);
    };

    loadMessages();

    return () => {
      isCancelled = true;
    };
  }, [
    organizationIdentifier,
    roomId,
    selectedRoom,
    messagesRefresh,
    setMessagesByRoom,
    userJson?.userIdentifier,
  ]);

  const isGroupChat = selectedRoom?.chatType === "group";
  const roomMembers = useMemo(
    () => buildRoomMemberDirectory(selectedRoom?.members ?? [], displayMessages),
    [displayMessages, selectedRoom?.members],
  );

  if (isLoading && !displayMessages.length)
    return <Spin size="medium" className="spinnerCenter" />;

  if (!displayMessages.length) {
    return (
      <div className="message-list-container chat-scroll-y">
        <div className="message-list-empty">
          <p>No messages yet</p>
          <p>Start the conversation!</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="message-list-container chat-scroll-y"
      role="log"
      aria-live="polite"
      onScroll={handleScroll}
    >
      {isLoadingOlderMessages ? (
        <div className="message-list-older-loader" aria-busy="true" aria-live="polite">
          <Spin size="small" />
        </div>
      ) : null}
      {displayMessages.map((item, index) => {
        if (item.roomInfo) {
          return (
            <MessageItem
              key={item.messageId}
              message={item}
              currentUserId={currentUserId}
              roomMembers={roomMembers}
              startOfRun
            />
          );
        }

        const isOutgoing = String(item.sender) === String(currentUserId);
        const prev = index > 0 ? displayMessages[index - 1] : null;
        const startOfRun = !prev || String(prev.sender) !== String(item.sender);
        const showAuthorMeta =
          !isOutgoing &&
          startOfRun &&
          isGroupChat &&
          Boolean(item.senderName || item.sender);

        return (
          <MessageItem
            key={item.messageId}
            message={item}
            isOutgoing={isOutgoing}
            showAuthorMeta={showAuthorMeta}
            startOfRun={startOfRun}
            currentUserId={currentUserId}
            roomMembers={roomMembers}
            onEdit={isOutgoing ? handleEditMessage : undefined}
            onReact={handleReactToMessage}
            isReacting={String(reactingMessageId) === String(item.messageId)}
          />
        );
      })}
      <div ref={messagesEndRef} className="message-list-end-anchor" />
    </div>
  );
};

export default MessageList;
